<template>
  <div class="custom-progress-container">
    <!-- Progress Bar -->
    <div 
      class="custom-progress-bar"
      :class="[
        size === 'sm' ? 'custom-progress-bar-sm' : size === 'lg' ? 'custom-progress-bar-lg' : 'custom-progress-bar-md'
      ]"
    >
      <div 
        class="custom-progress-fill"
        :class="[
          color_class,
          animated ? 'custom-progress-animated' : ''
        ]"
        :style="{ width: percentage + '%' }"
      ></div>
    </div>
    
    <!-- Percentage Label -->
    <span 
      v-if="show_label"
      class="custom-progress-label"
      :class="[
        size === 'sm' ? 'custom-progress-label-sm' : size === 'lg' ? 'custom-progress-label-lg' : 'custom-progress-label-md'
      ]"
    >
      {{ Math.round(percentage) }}%
    </span>

    <!-- Custom Label -->
    <span 
      v-if="custom_label"
      class="custom-progress-custom-label"
      :class="[
        size === 'sm' ? 'custom-progress-custom-label-sm' : size === 'lg' ? 'custom-progress-custom-label-lg' : 'custom-progress-custom-label-md'
      ]"
    >
      {{ custom_label }}
    </span>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'BaseProgressBar',
  props: {
    percentage: {
      type: Number,
      required: true,
      validator: (value) => value >= 0 && value <= 100,
    },
    color: {
      type: String,
      default: 'emerald',
      validator: (value) => [
        'emerald', 'blue', 'indigo', 'purple', 'pink', 
        'red', 'orange', 'yellow', 'green', 'gray'
      ].includes(value),
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    show_label: {
      type: Boolean,
      default: true,
    },
    custom_label: {
      type: String,
      default: null,
    },
    animated: {
      type: Boolean,
      default: false,
    },
  },
  
  setup(props) {
    const color_class = computed(() => {
      const colors = {
        emerald: 'custom-progress-color-emerald',
        blue: 'custom-progress-color-blue',
        indigo: 'custom-progress-color-indigo',
        purple: 'custom-progress-color-purple',
        pink: 'custom-progress-color-pink',
        red: 'custom-progress-color-red',
        orange: 'custom-progress-color-orange',
        yellow: 'custom-progress-color-yellow',
        green: 'custom-progress-color-green',
        gray: 'custom-progress-color-gray',
      };
      
      return colors[props.color] || colors.emerald;
    });

    return {
      color_class,
    };
  },
};
</script>

<style scoped>
/* Progress Container */
.custom-progress-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Progress Bar Base */
.custom-progress-bar {
  flex: 1;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  position: relative;
}

.custom-progress-bar-sm {
  height: 0.375rem;
}

.custom-progress-bar-md {
  height: 0.5rem;
}

.custom-progress-bar-lg {
  height: 0.75rem;
}

/* Progress Fill */
.custom-progress-fill {
  height: 100%;
  transition: all 0.5s ease-out;
  border-radius: 9999px;
  background-color: #10b981;
  position: relative;
  min-width: 0;
}

/* Color Classes */
.custom-progress-color-emerald {
  background-color: #10b981 !important;
}

.custom-progress-color-blue {
  background-color: #3b82f6 !important;
}

.custom-progress-color-indigo {
  background-color: #6366f1 !important;
}

.custom-progress-color-purple {
  background-color: #8b5cf6 !important;
}

.custom-progress-color-pink {
  background-color: #ec4899 !important;
}

.custom-progress-color-red {
  background-color: #dc2626 !important;
}

.custom-progress-color-orange {
  background-color: #ea580c !important;
}

.custom-progress-color-yellow {
  background-color: #eab308 !important;
}

.custom-progress-color-green {
  background-color: #16a34a !important;
}

.custom-progress-color-gray {
  background-color: #6b7280 !important;
}

/* Animation */
.custom-progress-animated {
  animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes custom-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Labels */
.custom-progress-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  font-variant-numeric: tabular-nums;
  min-width: 3rem;
  text-align: right;
}

.custom-progress-label-sm {
  font-size: 0.75rem;
}

.custom-progress-label-lg {
  font-size: 1rem;
}

.custom-progress-custom-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
}

.custom-progress-custom-label-sm {
  font-size: 0.75rem;
}

.custom-progress-custom-label-lg {
  font-size: 1rem;
}
</style>
